import { ReactNode } from 'react';
import { useUserRole } from 'src/hooks/useUserRole';
import { Role } from 'src/@types/role';
import { useOrg, useOrgs } from 'src/hooks/query/orgHooks';
import { Button, Card, CardHeader, Modal, Stack, Typography } from '@mui/material';
import NavOrgSwitcher from 'src/layouts/app/nav/NavOrgSwitcher';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useIntercom } from 'react-use-intercom';
import { resetMixpanel } from 'src/utils/analytics';

type BillingGuardProps = {
  children: ReactNode;
};

export default function BillingGuard({ children }: BillingGuardProps) {
  const { data: userRole } = useUserRole();
  const { logout } = useAuth0();
  const { data: currentOrg } = useOrg();
  const { data: orgs = [], isPending: orgsLoading } = useOrgs();

  const { show } = useIntercom(); // Use the Intercom hook to access the show method

  const handleLogout = async () => {
    try {
      logout({ logoutParams: { returnTo: window.location.origin } });
      resetMixpanel();
      localStorage.removeItem('lastUserId');
    } catch (error) {
      console.error(error);
      toast.error('Unable to logout!');
    }
  };

  const showDisabledModal =
    userRole && userRole !== Role.NuminarAdmin && currentOrg && currentOrg?.is_disabled;

  if (showDisabledModal) {
    return (
      <>
        {children}
        <Modal open={true}>
          <Card
            sx={{
              position: 'absolute',
              maxWidth: 450,
              top: { lg: '25%', md: '15%', sm: '6%', xs: '4%' },
              left: { lg: '35%', md: '20%', sm: '10%', xs: '3%' },
              right: { lg: '25%', md: '20%', sm: '10%', xs: '3%' },
              p: 3,
              outline: 'none',
            }}
          >
            <Stack
              sx={{
                width: '100%',
                justifyContent: 'center',
                flexDirection: 'row',
                marginBottom: 2,
              }}
            >
              <ExclamationCircleIcon style={{ height: 72, width: 72, color: '#CCC' }} />
            </Stack>

            <CardHeader sx={{ pt: 0, mb: 2, textAlign: 'center' }} title="Account Suspended" />

            <Stack direction="column" gap={3}>
              <Typography variant="body2" sx={{ width: '100%', textAlign: 'center' }}>
                Access to the <b>{currentOrg.name}</b> organization has been placed on hold. Please
                reach out to our support team with any questions.
              </Typography>
              <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
                <Button onClick={handleLogout} variant="outlined" color="primary" size="small">
                  Log Out
                </Button>
                {!orgsLoading && orgs.length > 1 && (
                  <NavOrgSwitcher triggerComponent={OrgSwitcherButton} />
                )}

                <Button onClick={show} variant="contained" color="primary" size="small">
                  Contact Support
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Modal>
      </>
    );
  }

  return <>{children}</>;
}

const OrgSwitcherButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <Button onClick={onClick} variant="outlined" color="primary" size="small">
      Switch Organization
    </Button>
  );
};
